import React, { useState } from 'react';
import { Box, Typography, IconButton, Drawer } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import logo from '../../assets/images/logo-white.png';
import { colors, fonts } from '../../theme';
import { SHOW_EVENT_SCHEDULE } from '../../config';

// Ordered to match the order sections actually appear on the page.
const navLinks = [
  { label: 'About', href: '#about' },
  { label: 'Impact', href: '#impact' },
  { label: 'Schedule', href: '#schedule' },
  { label: 'FAQ', href: '#faq' },
  { label: 'Sponsors', href: '#sponsors' },
].filter(({ label }) => (SHOW_EVENT_SCHEDULE || label !== 'Schedule'));

const registerHref =
  'https://docs.google.com/forms/d/e/1FAIpQLScs53tAUBWrcpVwE0nnHcP-pl1cv4yCS7sUNJzrgMIR-kjPmw/viewform?usp=sharing&ouid=110276353823843300021';

const mlhBadgeHref =
  'https://mlh.io/na?utm_source=na-hackathon&utm_medium=TrustBadge&utm_campaign=2026-season&utm_content=white';
const mlhBadgeSrc = 'https://logged-assets.s3.amazonaws.com/trust-badge/2027/mlh-trust-badge-2027-white.svg';

const Navigation = () => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      <Box
        component="nav"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          height: '75px',
          backgroundColor: colors.navSurface,
          boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2, sm: 3, md: '32px' },
          gap: { xs: 1, md: 4 },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '8px', md: '28px' }, minWidth: 0 }}>
          {/* Mobile menu toggle */}
          <IconButton
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            onClick={() => setMobileOpen((prev) => !prev)}
            sx={{
              display: { xs: 'inline-flex', md: 'none' },
              color: colors.offWhite,
              flexShrink: 0,
            }}
          >
            {mobileOpen ? <CloseIcon /> : <MenuIcon />}
          </IconButton>

          {/* Logo + wordmark */}
          <Box
            component="a"
            href="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: '12px', md: '28px' },
              textDecoration: 'none',
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt="CSE Society logo"
              sx={{ height: { xs: '32px', md: '40px' }, objectFit: 'contain', flexShrink: 0 }}
            />
            <Typography
              sx={{
                fontFamily: fonts.heading,
                fontWeight: 700,
                fontSize: { xs: '14px', sm: '16px', md: '20px' },
                lineHeight: '32px',
                color: colors.purple,
                whiteSpace: 'nowrap',
                // Phones can't fit menu + logo + wordmark + MLH badge + Register in one row;
                // drop the wordmark there rather than let the buttons sit on top of it.
                '@media (max-width: 439.95px)': { display: 'none' },
              }}
            >
              HACKATHON
            </Typography>
          </Box>
        </Box>

        {/* Nav links (desktop) */}
        <Box
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 4,
          }}
        >
          {navLinks.map(({ label, href }) => (
            <Typography
              key={label}
              component="a"
              href={href}
              sx={{
                color: colors.offWhite,
                fontFamily: fonts.mono,
                fontSize: '16px',
                textDecoration: 'none',
                '&:hover': { color: colors.gold },
              }}
            >
              {label}
            </Typography>
          ))}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '8px', md: '16px' }, flexShrink: 0 }}>
        {/* Register button */}
        <Box
          component="a"
          href={registerHref}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            flexShrink: 0,
            textDecoration: 'none',
            backgroundColor: colors.gold,
            color: colors.white,
            fontFamily: fonts.mono,
            fontWeight: 400,
            fontSize: { xs: '13px', sm: '14px', md: '16px' },
            lineHeight: '22px',
            padding: { xs: '8px 14px 9px', md: '12px 24px 13px' },
            borderRadius: '4px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
            animation: 'ctaPulse 2.4s ease-in-out infinite',
            transition: 'transform 0.25s ease, box-shadow 0.25s ease',
            '&:hover': {
              transform: 'scale(1.07)',
              animation: 'none',
              boxShadow: `0 0 10px rgba(235, 177, 17, 0.55)`,
            },
          }}
        >
          Register
        </Box>

        {/* MLH trust badge — the slot reserves its width in the row, while the badge itself is
            pinned to the top of the nav and taller than it, so it hangs down past the bar. */}
        <Box
          sx={{
            position: 'relative',
            alignSelf: 'stretch',
            flexShrink: 0,
            width: { xs: '60px', md: '100px' },
          }}
        >
          <Box
            component="a"
            id="mlh-trust-badge"
            href={mlhBadgeHref}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              display: 'block',
              filter: 'drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.25))',
            }}
          >
            <Box
              component="img"
              src={mlhBadgeSrc}
              alt="Major League Hacking 2026 Hackathon Season"
              sx={{ width: '100%', display: 'block' }}
            />
          </Box>
        </Box>
        </Box>
      </Box>

      {/* Nav links (mobile drawer) */}
      <Drawer
        anchor="top"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            top: '75px',
            backgroundColor: colors.navSurface,
            borderTop: '1px solid rgba(239, 239, 239, 0.1)',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', py: 1 }}>
          {navLinks.map(({ label, href }) => (
            <Typography
              key={label}
              component="a"
              href={href}
              onClick={() => setMobileOpen(false)}
              sx={{
                color: colors.offWhite,
                fontFamily: fonts.mono,
                fontSize: '18px',
                textDecoration: 'none',
                px: 3,
                py: 1.75,
                '&:hover': { color: colors.gold },
              }}
            >
              {label}
            </Typography>
          ))}
        </Box>
      </Drawer>
    </>
  );
};

export default Navigation;
